import {generateUuidV4} from "./strings.ts";
import {wait} from "./date.ts";

const basePath = 'api/switchio/pay/v6'
const ENDPOINTS = {
  versionInfo: 'info', // get
  terminalStatus: 'terminal', // post
  operationStatus: 'status', // post; fetch every 3s after start of creation of payment/refund/reverse/closing
  operationResult: 'result', // post; fetch result after end of payment/refund/reverse/closing
  lastOperationResult: 'last_transaction', // post; fetch result of last operation
  allOperationsResult: 'transactions', // post; fetch result of list of successful operations
  confirmTransaction: 'confirm', // post; confirm transaction in case it's needed (req. signature, etc.) - have to be confirmed max 60s after transaction is created, otherwise is canceled automagically
  cancelTransaction: 'cancel', // post; cancel [external?] transaction (from another terminal) in states "WaitForCard" or "WaitForUserInput," otherwise, the request is rejected.
  daughterCompanies: 'daughter_companies', // post; get list of daughter company ids, if virtualization enabled
  closing: 'closing', // post; initiate checkout closing
  cardVerify: 'card_verify', // post; verify card and get token if successful
  createPayment: 'payment', // post; create payment transaction
  createRefund: 'refund', // post; create refund transaction
  createReferralRefund: 'referral_refund', // post; create referral refund transaction
  reversePayment: 'reverse', // post; create reverse transaction
  createPreauthTx: 'preauth', // post; create preauth transaction todo do we need to implement this? nope.
  createQrPayment: 'qrpayment', // post; create qr payment tx

}
const OPERATION_STATUS = {
  Idle: 'Idle',
  WaitForCard: 'WaitForCard',
  WaitForPin: 'WaitForPin',
  Connecting: 'Connecting',
  Connected: 'Connected',
  Disconnected: 'Disconnected',
  Sending: 'Sending',
  Receiving: 'Receiving',
  Finished: 'Finished',
  FinishedPlain: 'FinishedPlain',
}
type TX_TYPE = 'payment' | 'qrPayment' | 'refund'
type CommonPaymentInit = {
  transactionId?: string
  amountFloat: number
  currencyId: string
  invoiceNumber: string
}
type ComgatePaymentInit = CommonPaymentInit & { type: TX_TYPE }

export class ComgatePayment {
  txType: TX_TYPE
  transactionId: string
  amountFloat: number
  currencyId: string
  invoiceNumber: string
  operationStatus: string
  operationResult: any
  isConfirmed: boolean
  constructor(init: ComgatePaymentInit) {
    this.txType = init.type
    this.transactionId = init.transactionId
    this.amountFloat = init.amountFloat
    this.currencyId = init.currencyId
    this.invoiceNumber = init.invoiceNumber
  }
}
export class PaymentTerminal {
  ip: string
  port: number
  secureString: string = '27924505'
  isLoggingEnabled: boolean = false
  lastOperation: ComgatePayment
  constructor(ipAddress: string, port: number, isLoggingEnabled: boolean = false) {
    this.ip = ipAddress
    this.port = port
    this.isLoggingEnabled = isLoggingEnabled
  }

  async startPayment(init: CommonPaymentInit) {
    // todo
    // createPayment
    // fetch operationStatus until status === Finished
    // get operationResult
    // confirmOperation within 60s!
    if (!init.transactionId) {
      init.transactionId = generateUuidV4()
    }
    const payment = new ComgatePayment({ ...init, type: 'payment' })
    this.lastOperation = payment
    const createPaymentResult = await this.createPayment(init)
    console.log({createPaymentResult}) // todo
    if (createPaymentResult?.status === 'Server busy') {
      console.log('server busy, repeat!')
      await wait(500)
      return this.startPayment(init)
    }
    return new Promise(resolve => {
      const fetchStatusInterval = setInterval(async () => {
        payment.operationStatus = await this.getOperationStatus(init.transactionId)
        if (payment.operationStatus === OPERATION_STATUS.Finished) {
          clearInterval(fetchStatusInterval)
          payment.operationResult = await this.getOperationResult(init.transactionId)
          if (payment.operationResult?.responseCode === 'OK') { // todo test!
            payment.isConfirmed = await this.confirmTransaction(init.transactionId)
          }
          resolve(payment)
        }
      }, 3000)
    });
  }

  async startQrPayment(init: CommonPaymentInit) {
    if (!init.transactionId) {
      init.transactionId = generateUuidV4()
    }
    const qrPayment = new ComgatePayment({ ...init, type: 'qrPayment' })
    this.lastOperation = qrPayment
    const createQrPaymentResult = await this.createQrPayment(init)
    console.log({createQrPaymentResult}) // todo
    return new Promise(resolve => {
      const fetchStatusInterval = setInterval(async () => {
        qrPayment.operationStatus = await this.getOperationStatus(init.transactionId)
        if (qrPayment.operationStatus === OPERATION_STATUS.Finished) {
          clearInterval(fetchStatusInterval)
          qrPayment.operationResult = await this.getOperationResult(init.transactionId)
          if (qrPayment.operationResult?.responseCode === 'OK') { // todo test!
            qrPayment.isConfirmed = await this.confirmTransaction(init.transactionId)
          }
          resolve(qrPayment)
        }
      }, 3000)
    })
  }

  async startRefund(init: CommonPaymentInit) {
    if (!init.transactionId) {
      init.transactionId = generateUuidV4()
    }
    const refund = new ComgatePayment({ ...init, type: 'refund' })
    this.lastOperation = refund
    const createRefundResult = await this.createRefund(init)
    console.log({createRefundResult}) // todo
    return new Promise(resolve => {
      const fetchStatusInterval = setInterval(async () => {
        refund.operationStatus = await this.getOperationStatus(init.transactionId)
        if (refund.operationStatus === OPERATION_STATUS.Finished) {
          clearInterval(fetchStatusInterval)
          refund.operationResult = await this.getOperationResult(init.transactionId)
          if (refund.operationResult?.responseCode === 'OK') { // todo test!
            refund.isConfirmed = await this.confirmTransaction(init.transactionId)
          }
          resolve(refund)
        }
      }, 3000)
    })
  }

  async cancelCurrentOperation() {
    const res = this.cancelTransaction(this.lastOperation.transactionId)
    this.log('cancelCurrentOperation: ', {res})
  }

  async startOperationReversal(originalTransactionId?: string) {
    const transactionId = generateUuidV4()
    if (!originalTransactionId) {
      originalTransactionId = this.lastOperation.transactionId
    }
    const reversalResult = await this.reversePayment(transactionId, originalTransactionId)
    console.log({reversalResult}) // todo
    return new Promise(resolve => {
      const fetchStatusInterval = setInterval(async () => {
        const operationStatus = await this.getOperationStatus(transactionId)
        if (operationStatus === OPERATION_STATUS.Finished) {
          clearInterval(fetchStatusInterval)
          const operationResult = await this.getOperationResult(transactionId)
          // there should be no confirmation needed
          resolve(operationResult)
        }
      }, 3000)
    })
  }

  private async reversePayment(transactionId: string, originalTransactionId?: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.reversePayment}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId,
          originalTransactionId, // if not defined, last confirmed tx will be reversed
          // daughterCompanyId if virtualization enabled
        },
      })
    }).then(resp => resp.json())
    this.log('createRefund:', {res})
    return res
  }

  private async createQrPayment(init: CommonPaymentInit): Promise<any> {
    const amount = Number.isInteger(init.amountFloat * 100) ? init.amountFloat * 100 : Math.floor(init.amountFloat * 100) // Amount of the payment, in cents 10053 is 100.53 euro
    const currencyCode = this.getCurrencyCode(init.currencyId)
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.createQrPayment}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId: init.transactionId,
          amount,
          currencyCode,
          invoiceNumber: init.invoiceNumber,
          // daughterCompanyId if virtualization enabled
        },
      })
    }).then(resp => resp.json())
    this.log('createQrPayment:', {res})
    return res
  }

  private async createRefund(init: CommonPaymentInit): Promise<any> {
    const amount = Number.isInteger(init.amountFloat * 100) ? init.amountFloat * 100 : Math.floor(init.amountFloat * 100) // Amount of the refund, in cents 10053 is 100.53 euro
    const currencyCode = this.getCurrencyCode(init.currencyId)
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.createRefund}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId: init.transactionId,
          amount,
          currencyCode,
          invoiceNumber: init.invoiceNumber,
          // daughterCompanyId if virtualization enabled
        },
      })
    }).then(resp => resp.json())
    this.log('createRefund:', {res})
    return res
  }

  private async createReferralRefund(transactionId: string, amountFloat: number, currencyId: string, invoiceNumber: string, refundId: string): Promise<any> {
    const amount = Number.isInteger(amountFloat * 100) ? amountFloat * 100 : Math.floor(amountFloat * 100) // Amount of the refund, in cents 10053 is 100.53 euro
    const currencyCode = this.getCurrencyCode(currencyId)
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.createReferralRefund}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId,
          amount,
          currencyCode,
          invoiceNumber,
          refundId,
          // daughterCompanyId if virtualization enabled
        },
      })
    }).then(resp => resp.json())
    this.log('createReferralRefund:', {res})
    return res
  }

  private async createPayment(init: CommonPaymentInit): Promise<any> {
    const amount = Number.isInteger(init.amountFloat * 100) ? init.amountFloat * 100 : Math.floor(init.amountFloat * 100) // Amount of the payment, in cents 10053 is 100.53 euro
    const currencyCode = this.getCurrencyCode(init.currencyId)
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.createPayment}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId: init.transactionId,
          amount,
          currencyCode,
          invoiceNumber: init.invoiceNumber,
          // daughterCompanyId if virtualization enabled
        },
      })
    }).then(resp => resp.json())
    this.log('createPayment:', {res})
    return res
  }

  async verifyCard(transactionId: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.cardVerify}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        transactionId,
      })
    }).then(resp => resp.json())
    this.log('verifyCard:', {res})
    return res
  }

  async getDaughterCompanies(): Promise<Array<any>> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.daughterCompanies}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
      })
    }).then(resp => resp.json())
    this.log('getDaughterCompanies:', {res})
    return res
  }

  async closeCheckout(transactionId: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.closing}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        request: {
          transactionId,
          // daughterCompanyId if virtualization enabled
        }
      })
    }).then(resp => resp.json())
    this.log('closeCheckout:', {res})
    return res
  }

  async confirmTransaction(transactionId: string, confirm: boolean = true): Promise<boolean> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.confirmTransaction}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        transactionId,
        confirm,
      })
    }).then(resp => resp.json())
    this.log('confirmTransaction:', {res})
    return res?.isConfirmed
  }

  private async cancelTransaction(transactionId: string): Promise<boolean> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.cancelTransaction}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        transactionId,
      })
    }).then(resp => resp.json())
    this.log('cancelTransaction:', {res})
    return res
  }

  async getAllOperationsResult(): Promise<Array<any>> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.allOperationsResult}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
      })
    }).then(resp => resp.json())
    this.log('getAllOperationsResult:', {res})
    return res
  }

  async getLastOperationResult(): Promise<any> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.lastOperationResult}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
      })
    }).then(resp => resp.json())
    this.log('getLastOperationResult:', {res})
    return res
  }

  async getOperationResult(transactionId: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.operationResult}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        transactionId,
      })
    }).then(resp => resp.json())
    this.log('getOperationResult:', {res})
    return res
  }

  async getOperationStatus(transactionId: string): Promise<string> {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.operationStatus}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        transactionId,
      })
    }).then(resp => resp.json())
    this.log('getOperationStatus:', {res})
    return res?.status
  }

  async getTerminalStatus() {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.versionInfo}`, {
      method: 'post',
      body: JSON.stringify({
        secureString: this.secureString,
        checkOnline: true,
      })
    }).then(resp => resp.json())
    // const res = await fetch(`http://192.168.1.167:33350/api/switchio/pay/v6`, {
    //   method: 'post',
    //   body: JSON.stringify({
    //     secureString: '27924505',
    //     checkOnline: true,
    //   })
    // }).then(resp => resp.json())
    this.log('getTerminalStatus', {res})
    return res
  }

  async getVersionInfo() {
    const res = await fetch(`${this.baseUrl}${ENDPOINTS.terminalStatus}`)
      .then(resp => resp.json())
    this.log('getVersionInfo', {res})
    return res
  }

  get baseUrl(): string {
    // yep, http is correct - this pos doesn't allow for tls connection 💩
    return `http://${this.ip}:${this.port}/${basePath}/`
  }

  getCurrencyCode(currencyId: string): number {
    if (currencyId?.toLowerCase() === 'czk') {
      return 203
    } else if (currencyId?.toLowerCase() === 'eur') {
      return 978
    }
    return 203 // default to CZK; comgate supports only CZK and EUR
  }

  log(...args: any) {
    if (this.isLoggingEnabled) {
      console.log(args)
    }
  }
}
